import { Duration, LocalDate, LocalTime, Period } from '@js-joda/core';

import {
   Building,
   CompleteAddress,
   Country,
   Flat,
   PartialAddress,
   Postcode,
   Street,
   Town,
} from '@dreryk/address-common-api';
import { Color } from '@dreryk/color-common-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitId } from '@dreryk/egabinet-visit-published-language-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import {
   Icd10Code,
   Icd10Entry,
   Icd10Name,
   Icd9Code,
   Icd9Entry,
   Icd9Name,
} from '@dreryk/egabinet-icd-dictionary-published-language-api';
import {
   AuthorizedPersonExternalIdentifier,
   GuardianExternalIdentifier,
   IdentityDocument,
   MedicalRecordNumber,
   PatientExternalIdentifier,
   PatientId,
   PatientReferenceIdentifier,
} from '@dreryk/egabinet-patient-published-language-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { TherapeuticGroupId } from '@dreryk/egabinet-group-session-published-language-api';
import {
   ReceptionServiceDuration,
   ReceptionServiceId,
} from '@dreryk/egabinet-private-medical-service-published-language-api';
import { EmailAddress } from '@dreryk/email-address-common-api';
import { HealthcareProviderRegisterBookNumber } from '@dreryk/healthcare-provider-register-book-number-api';
import { MedicalLicenceNumber } from '@dreryk/medical-licence-number-common-api';
import { Money } from '@dreryk/money-common-api';
import { FirstName, LastName, MiddleName } from '@dreryk/person-name-common-api';
import {
   GuardianPesel,
   NationalIdentificationNumber,
   NationalIdentificationNumberCountry,
   Passport,
   Pesel,
} from '@dreryk/personal-external-identifier-common-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';
import { Regon, UnitRegon } from '@dreryk/regon-common-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';
import {
   HealthcareFacilityLocationSpecialtyCode,
   ServiceLocationId,
} from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { registerDeserializer, registerSerializer } from '@dreryk/json-class-mapper-client';
import {
   DurationDTO,
   DurationSerialization,
   LocalDateSerialization,
   LocalTimeSerialization,
   PeriodDTO,
   PeriodSerialization,
} from '@dreryk/date-json-serialization-common';
import { Range } from '@dreryk/watermelon';

registerSerializer(Period, (period) => PeriodSerialization.toJSON(period));
registerDeserializer(PeriodDTO.CLASS, (json) => {
   const dto = (json as unknown) as PeriodDTO;
   return PeriodSerialization.fromJSON(dto);
});
registerSerializer(Duration, (duration) => DurationSerialization.toJSON(duration));
registerDeserializer(DurationDTO.CLASS, (json) => {
   const dto = (json as unknown) as DurationDTO;
   return DurationSerialization.fromJSON(dto);
});
registerSerializer(LocalDate, (localDate) => LocalDateSerialization.toJSON(localDate));
registerDeserializer(LocalDateSerialization.CLASS, (json) => LocalDateSerialization.fromJSON(json));
registerSerializer(LocalTime, (localTime) => LocalTimeSerialization.toJSON(localTime));
registerDeserializer(LocalTimeSerialization.CLASS, (json) => LocalTimeSerialization.fromJSON(json));
registerDeserializer(EmailAddress.CLASS, (obj) => EmailAddress.fromJSON(obj));
registerDeserializer(GuardianExternalIdentifier.CLASS, (obj) => GuardianExternalIdentifier.fromJSON(obj));
registerDeserializer(AuthorizedPersonExternalIdentifier.CLASS, (obj) => {
   return AuthorizedPersonExternalIdentifier.fromJSON(obj);
});
registerDeserializer(GuardianPesel.CLASS, (obj) => GuardianPesel.fromJSON(obj));
registerDeserializer(MedicalRecordNumber.CLASS, (obj) => MedicalRecordNumber.fromJSON(obj));
registerDeserializer(HealthcareProviderRegisterBookNumber.CLASS, (obj) => {
   return HealthcareProviderRegisterBookNumber.fromJSON(obj);
});
registerDeserializer(MedicalLicenceNumber.CLASS, (obj) => MedicalLicenceNumber.fromJSON(obj));
registerDeserializer(PatientExternalIdentifier.CLASS, (obj) => PatientExternalIdentifier.fromJSON(obj));
registerDeserializer(PatientReferenceIdentifier.CLASS, (obj) => PatientReferenceIdentifier.fromJSON(obj));
registerDeserializer(Pesel.CLASS, (obj) => Pesel.fromJSON(obj));
registerDeserializer(PhoneNumber.CLASS, (obj) => PhoneNumber.fromJSON(obj));
registerDeserializer(Postcode.CLASS, (obj) => Postcode.fromJSON(obj));
registerDeserializer(Regon.CLASS, (obj) => Regon.fromJSON(obj));
registerDeserializer(UnitRegon.CLASS, (obj) => UnitRegon.fromJSON(obj));
registerDeserializer(TaxIdentifier.CLASS, (obj) => TaxIdentifier.fromJSON(obj));
registerDeserializer(Country.CLASS, (obj) => Country.fromJSON(obj));
registerDeserializer(Town.CLASS, (obj) => Town.fromJSON(obj));
registerDeserializer(Street.CLASS, (obj) => Street.fromJSON(obj));
registerDeserializer(Building.CLASS, (obj) => Building.fromJSON(obj));
registerDeserializer(Flat.CLASS, (obj) => Flat.fromJSON(obj));
registerDeserializer(CompleteAddress.CLASS, (obj) => CompleteAddress.fromJSON(obj));
registerDeserializer(PartialAddress.CLASS, (obj) => PartialAddress.fromJSON(obj));
registerDeserializer(FirstName.CLASS, (obj) => FirstName.fromJSON(obj));
registerDeserializer(MiddleName.CLASS, (obj) => MiddleName.fromJSON(obj));
registerDeserializer(LastName.CLASS, (obj) => LastName.fromJSON(obj));
registerDeserializer(Passport.CLASS, (obj) => Passport.fromJSON(obj));
registerDeserializer(NationalIdentificationNumber.CLASS, (obj) => NationalIdentificationNumber.fromJSON(obj));
registerDeserializer(NationalIdentificationNumberCountry.CLASS, (obj) => {
   return NationalIdentificationNumberCountry.fromJSON(obj);
});
registerDeserializer(Range.CLASS, (obj) => Range.fromJSON(obj));
registerDeserializer(IdentityDocument.CLASS, (obj) => IdentityDocument.fromJSON(obj));
registerDeserializer(Money.CLASS, (obj) => Money.fromJSON(obj));
registerDeserializer(Color.CLASS, (obj) => Color.fromJSON(obj));
registerDeserializer(Icd10Entry.CLASS, (obj) => Icd10Entry.fromJSON(obj));
registerDeserializer(Icd10Code.CLASS, (obj) => Icd10Code.fromJSON(obj));
registerDeserializer(Icd10Name.CLASS, (obj) => Icd10Name.fromJSON(obj));
registerDeserializer(Icd9Entry.CLASS, (obj) => Icd9Entry.fromJSON(obj));
registerDeserializer(Icd9Code.CLASS, (obj) => Icd9Code.fromJSON(obj));
registerDeserializer(Icd9Name.CLASS, (obj) => Icd9Name.fromJSON(obj));
registerDeserializer(TimeSlot.CLASS, (obj) => TimeSlot.fromJSON(obj));
registerDeserializer(PractitionerId.CLASS, (obj) => PractitionerId.fromJSON(obj));
registerDeserializer(ReceptionServiceId.CLASS, (obj) => ReceptionServiceId.fromJSON(obj));
registerDeserializer(ReceptionServiceDuration.CLASS, (obj) => ReceptionServiceDuration.fromJSON(obj));
registerDeserializer(PurchaseId.CLASS, (obj) => PurchaseId.fromJSON(obj));
registerDeserializer(AppointmentId.CLASS, (obj) => AppointmentId.fromJSON(obj));
registerDeserializer(PatientId.CLASS, (obj) => PatientId.fromJSON(obj));
registerDeserializer(HealthcareFacilityLocationSpecialtyCode.CLASS, (obj) =>
   HealthcareFacilityLocationSpecialtyCode.fromJSON(obj)
);
registerDeserializer(TherapeuticGroupId.CLASS, (obj) => TherapeuticGroupId.fromJSON(obj));
registerDeserializer(ServiceLocationId.CLASS, (obj) => ServiceLocationId.fromJSON(obj));
registerDeserializer(VisitId.CLASS, (obj) => VisitId.fromJSON(obj));
